import type { Directive, DirectiveBinding, ComponentPublicInstance } from 'vue'
import { useUserStore } from '@/store/modules/user'
import { MenuType } from '@/enum/global-enums'
import { normalizeActionMark } from '@/utils/permissionMark'

type ActionValue = string | string[]

type RouteLike = {
  meta?: {
    actions?: string[]
  }
}

interface newEl extends HTMLElement {
  __v_action_display__: string
}

const DISPLAY_KEY = '__v_action_display__'

/**
 * 统一权限标记格式（收敛为 controller@action，兼容后端三段式 mark 与旧点号写法）
 */
const normalizeAction = normalizeActionMark

/**
 * 解析指令传入值
 * 支持字符串 / 字符串数组
 */
function getRequiredActions(value: ActionValue): string[] {
  if (typeof value === 'string') {
    const action = normalizeAction(value)
    return action ? [action] : []
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeAction(item)).filter(Boolean)
  }

  return []
}

/**
 * 从当前页面路由 meta.actions 取可用按钮权限
 */
function getRouteActions(binding: DirectiveBinding<ActionValue>): string[] {
  const instance = binding.instance as ComponentPublicInstance & { $route?: RouteLike }
  const actions = instance?.$route?.meta?.actions

  if (!Array.isArray(actions)) return []

  return actions.map((item) => normalizeAction(item)).filter(Boolean)
}

/**
 * 兜底：从用户全部按钮权限里取
 * 这样即使个别页面还没接好 meta.actions，旧页面也不会直接失效
 */
function getUserButtonActions(): string[] {
  const userStore = useUserStore()

  return (userStore.getPermissions || [])
    .filter((permission) => permission.type === MenuType.BUTTON_TYPE && permission.permission_mark)
    .map((permission) => normalizeAction(permission.permission_mark))
    .filter(Boolean)
}

/**
 * 默认：满足任意一个即可显示
 */
function hasPermission(requiredActions: string[], availableActions: string[]): boolean {
  if (!requiredActions.length) return false
  return requiredActions.some((action) => availableActions.includes(action))
}

function updateVisibility(el: HTMLElement, binding: DirectiveBinding<ActionValue>) {
  // 如果传入的数值为空，直接返回
  if (getRequiredActions(binding.value).length === 0) return

  const requiredActions = getRequiredActions(binding.value)

  if (!requiredActions.length) {
    throw new Error('v-action 需要传入权限标记，例如：v-action="\'settlementReceivable@add\'"')
  }

  // 记录元素原始 display，避免权限变化后无法恢复
  if (!(DISPLAY_KEY in el)) {
    ;(el as newEl)[DISPLAY_KEY] = el.style.display || ''
  }

  // 优先使用当前路由绑定的 actions
  const routeActions = getRouteActions(binding)

  // 若当前页面还没注入 meta.actions，则兜底走用户全量按钮权限
  const availableActions = routeActions.length ? routeActions : getUserButtonActions()

  const visible = hasPermission(requiredActions, availableActions)

  el.style.display = visible ? (el as newEl)[DISPLAY_KEY] : 'none'
}

const actionDirective: Directive<HTMLElement, ActionValue> = {
  mounted(el, binding) {
    updateVisibility(el, binding)
  },
  updated(el, binding) {
    updateVisibility(el, binding)
  }
}

export default actionDirective
